//  @flow
import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { useEffect, useState, useCallback, useMemo } from "react";

import {
  PlusOutlined,
  GroupOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  Page,
  Row,
  Col,
  Button,
  Input,
  Notification,
  Roller,
  AntdModal,
  Pace
} from "components/Atoms";

import * as _ from "lodash";

import * as Actions from "actions/seller/seller.products";
import * as ActionTypes from 'constants/ActionTypes';

import SellerProduct from "components/elements/seller/SellerProduct";
import SellerProductEntryForm from "components/elements/seller/SellerProductEntryForm";
import SellerCategorySelector from "components/elements/seller/SellerCategorySelector";

import "./styles.scss";

const ProductsMain = (props) => {
  const { 
    serviceError, 
    reqStatus, 
    isLoading,
    storeProducts, 
    categoryList,
    getStoreProductCategories, 
    getStoreProducts,
    updateProduct,
    history 
  } = props;

  const { count, records } = storeProducts;

  const [state, setState] = useState({
    bShowProductEntryDialog: false,
    bShowCategoryBrowser: false,
    selectedProduct: null,
    selectedCategory: null,
    filterText: ''
  });

  const { bShowProductEntryDialog, bShowCategoryBrowser, selectedProduct, selectedCategory } = state;

  // Memoized debounced filter function
  const debouncedFilterProducts = useMemo(
    () => _.debounce((value) => {
      setState(prev => ({ ...prev, filterText: value.toLowerCase() }));
    }, 1000),
    []
  );

  useEffect(() => {
    getStoreProductCategories();
    getStoreProducts(null, false);
  }, [getStoreProductCategories, getStoreProducts]);

  const sendNotification = useCallback((type, { title, description = "" }) => {
    Notification[type]({
      message: title,
      description,
    });
  }, []);

  useEffect(() => {
    if (serviceError) {
      const feedback = {
        title: "Something went wrong!",
        description: serviceError.statusText,
      };
      sendNotification("error", feedback);
    }
    switch (reqStatus) {
      case ActionTypes.UPDATE_PRODUCT_SUCCESS:
        // optimize later - updated product only
        getStoreProducts();
        break;
      default:
        break;
    }
  }, [serviceError, reqStatus, sendNotification, getStoreProducts]);

  const filterProducts = useCallback((value) => {
    debouncedFilterProducts(value);
  }, [debouncedFilterProducts]);

  const addProduct = useCallback(() => {
    history.push("/products/new");
  }, [history]);

  const editProduct = useCallback((product, prodId) => {
    product.id = prodId;
    updateProduct(product);
    hideProductEntryDialog();
  }, [updateProduct]);

  /* Header actions*/
  const showProductEntryDialog = useCallback((product) => () => {
    setState(prev => ({ 
      ...prev, 
      selectedProduct: product, 
      bShowProductEntryDialog: true 
    }));
  }, []);

  const hideProductEntryDialog = useCallback(() => {
    setState(prev => ({ 
      ...prev, 
      selectedProduct: null, 
      bShowProductEntryDialog: false 
    }));
  }, []);

  const showCategoryBrowser = useCallback(() => {
    setState(prev => ({ ...prev, bShowCategoryBrowser: true }));
  }, []);

  const hideCategoryBrowser = useCallback(() => {
    setState(prev => ({ ...prev, bShowCategoryBrowser: false }));
  }, []);

  const resetCategory = useCallback(() => {
    setState(prev => ({ ...prev, selectedCategory: null }));
    getStoreProducts(null);
  }, [getStoreProducts]);

  const setCategory = useCallback((category) => {
    setState(prev => ({ ...prev, selectedCategory: category, bShowCategoryBrowser: false }));
    getStoreProducts(category);
  }, [getStoreProducts]);

  const handlePage = useCallback((page) => (e) => {
    history.push({
      pathname: page
    });
  }, [history]);

  return (
    <Page className="products-home">
      <div className="content">
        {isLoading && <Pace color="#27ae60" height="2px" progressbar="2px"/>}
        <div className="product-list">
          <SellerCategorySelector
            title="All Product Categories"
            visible={bShowCategoryBrowser}
            categoryList={categoryList}
            handlePage={handlePage}
            resetCategory={resetCategory}
            onCategorySelect={setCategory}
          />
          <Row gutter={[16, 16]}>
            {records.map((product) => {
              return (
                <Col span={6} key={`pr-item-${product.id}`}>
                  <SellerProduct
                    className="product-item"
                    key={product.id}
                    data={product}
                    actions={
                      <>
                        <Button
                          type="primary"
                          onClick={showProductEntryDialog(product)}
                        >
                          Edit
                        </Button>
                      </>
                    }
                  />
                </Col>
              );
            })}
          </Row>
        </div>
      </div>
      <SellerProductEntryForm
        visible={bShowProductEntryDialog}
        data={selectedProduct}
        onOk={editProduct}
        onCancel={hideProductEntryDialog}
      />
    </Page>
  );
}
// Mapping Props and State.
const _ProductsMain = connect(
  ({ products, bootupInfo }) => ({
    bootupInfo: bootupInfo,
    reqStatus: products.reqStatus,
    isLoading: products.isLoading,
    serviceError: products.serviceError,
    storeProducts: products.storeProducts,
    categoryList: products.storeProductCategoryList,
  }),
  (dispatch) => bindActionCreators({ ...Actions }, dispatch)
)(ProductsMain);

export { _ProductsMain as default };
